import {
  Button, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, Spinner,
} from '@heroui/react';
import React, { FC } from 'react';

import { CampaignRecord, useGetCampaignRecipients } from '../../../queries';

interface CampaignStatsDrawerProps {
  campaign: CampaignRecord;
  onClose: () => void;
}

const formatDate = (value: number | null): string => (value
  ? new Date(value).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '');

const Stat: FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-lg border border-gray-200 px-3 py-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{value}</p>
    {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
  </div>
);

/** Statystyki jednej wysyłki: liczniki na górze, pod nimi lista odbiorców z otwarciami. */
export const CampaignStatsDrawer: FC<CampaignStatsDrawerProps> = ({ campaign, onClose }) => {
  const { data: recipients = [], isLoading } = useGetCampaignRecipients(campaign.id);

  /* Wskaźnik liczymy do WYSŁANYCH MAILI, nie do wszystkich odbiorców: przy kanale
     „tylko panel" nie ma czego otwierać i procent byłby mylący. */
  const rate = campaign.sentCount > 0
    ? `${Math.round((campaign.openedCount / campaign.sentCount) * 100)}%`
    : 'brak danych';

  const opened = recipients.filter((item) => item.openedAt);

  return (
    <Drawer
      isOpen
      placement="right"
      radius="none"
      classNames={{ base: 'w-full sm:w-1/2 sm:max-w-none' }}
      onClose={onClose}
    >
      <DrawerContent>
        <DrawerHeader className="flex-col items-start gap-1">
          <span className="text-base font-semibold text-gray-900">{campaign.subject}</span>
          <span className="text-xs font-normal text-gray-500">
            {`Wysłano ${formatDate(campaign.createdAt)}`}
          </span>
        </DrawerHeader>

        <DrawerBody className="gap-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Odbiorcy" value={String(campaign.recipientCount)} />
            <Stat label="Wysłane maile" value={String(campaign.sentCount)} />
            <Stat label="Otwarcia" value={String(campaign.openedCount)} hint={`Wskaźnik: ${rate}`} />
            <Stat label="W panelu" value={String(campaign.inboxCount)} />
          </div>

          {campaign.failedCount > 0 && (
            <p className="rounded-lg bg-danger-50 px-3 py-2 text-xs text-danger">
              {`Nie udało się wysłać ${campaign.failedCount} wiadomości. Powód widać przy odbiorcy poniżej.`}
            </p>
          )}

          <p className="text-xs text-gray-500">
            Otwarcia liczy niewidoczny obrazek w treści maila. Klienty z blokadą grafik go nie pobiorą,
            więc wynik jest zaniżony, a podgląd z prefetchem potrafi policzyć otwarcie bez udziału
            odbiorcy. To wskaźnik trendu, nie pomiar.
          </p>

          {isLoading && <Spinner size="sm" className="self-center" />}

          {!isLoading && recipients.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Adres</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Otwarcie</th>
                  </tr>
                </thead>
                <tbody>
                  {[...opened, ...recipients.filter((item) => !item.openedAt)].map((recipient) => (
                    <tr key={recipient.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{recipient.email}</td>
                      <td className="px-3 py-2">
                        {recipient.status === 'failed' ? (
                          <span className="text-danger" title={recipient.error}>błąd</span>
                        ) : (
                          <span className="text-gray-500">wysłane</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {recipient.openedAt
                          ? `${formatDate(recipient.openedAt)}${recipient.openCount > 1 ? ` (${recipient.openCount}x)` : ''}`
                          : 'brak'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DrawerBody>

        <DrawerFooter>
          <Button variant="light" onPress={onClose}>Zamknij</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
